/**
 * Edit Rotation Modal
 * Modal for editing existing rotation with timing configuration
 */

import React, { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useQueryClient } from '@tanstack/react-query'
import { Dialog, Button, FormItem, Typogaphy } from 'vnetwork-ui'
import { Plus, X } from 'lucide-react'

import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { Switch } from '@/components/ui/switch'
import { Select } from 'vnetwork-ui'

import { updateRotation } from '@/modules/Oncall/services'
import { ONCALL_QUERY_KEYS } from '@/modules/Oncall/constants'
import { Rotation } from '@/modules/Oncall/types/rotation'
import {
    UpdateRotationPayload,
    TimingType,
    ShiftFrequency,
    DayOfWeek,
    TimeSlotPayload,
} from '@/modules/Oncall/types/payload'

const DAYS_OF_WEEK: { value: DayOfWeek; label: string }[] = [
    { value: 0, label: 'sun' },
    { value: 1, label: 'mon' },
    { value: 2, label: 'tue' },
    { value: 3, label: 'wed' },
    { value: 4, label: 'thu' },
    { value: 5, label: 'fri' },
    { value: 6, label: 'sat' },
]

interface FormData {
    oncall_rotation_name: string
    shift_frequency: ShiftFrequency
    is_active: boolean
    timing_type: TimingType
    time_slots: TimeSlotPayload[]
}

export interface EditRotationModalProps {
    open: boolean
    onClose: () => void
    scheduleId: number
    rotation: Rotation
    refetch?: () => void
}

const editRotationSchema = yup.object().shape({
    oncall_rotation_name: yup.string().required('rotation_name_required'),
    shift_frequency: yup.string().required(),
    timing_type: yup.string().required(),
})

const EditRotationModal: React.FC<EditRotationModalProps> = ({
    open,
    onClose,
    scheduleId,
    rotation,
    refetch,
}) => {
    const { t: translate } = useTranslation('oncall')
    const queryClient = useQueryClient()

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { isSubmitting, errors },
    } = useForm<FormData>({
        mode: 'onSubmit',
        defaultValues: {
            oncall_rotation_name: '',
            shift_frequency: 'daily',
            is_active: true,
            timing_type: 'everyday',
            time_slots: [],
        },
        resolver: yupResolver(editRotationSchema),
    })

    // Debug: log errors if any
    React.useEffect(() => {
        if (Object.keys(errors).length > 0) {
            console.log('Form validation errors:', errors)
        }
    }, [errors])

    const watchTimingType = useWatch({ control, name: 'timing_type' })
    const watchTimeSlots = useWatch({ control, name: 'time_slots' }) || []
    const watchIsActive = useWatch({ control, name: 'is_active' })

    // Initialize form with rotation data
    useEffect(() => {
        if (rotation && open) {
            reset({
                oncall_rotation_name: rotation.oncall_rotation_name,
                shift_frequency: rotation.shift_frequency,
                is_active: rotation.is_active,
                timing_type: rotation.timing?.timing_type || 'everyday',
                time_slots: rotation.timing?.time_slots || [],
            })
        }
    }, [rotation, open, reset])

    const { mutateAsync: doUpdate, isPending: isUpdating } =
        useMutationWithHandler(
            {
                mutationFn: (payload: UpdateRotationPayload) =>
                    updateRotation({
                        scheduleId,
                        rotationId: rotation.oncall_rotation_id,
                        data: payload,
                    }),
            },
            {
                successMessage: translate(
                    'rotation_updated',
                    'Rotation updated'
                ),
                reactQueryKeys: [ONCALL_QUERY_KEYS.ROTATIONS],
            }
        )

    const addTimeSlot = () => {
        const newSlot: TimeSlotPayload = {
            day_from: 1,
            time_from: '08:00',
            day_to: 5,
            time_to: '18:00',
        }
        setValue('time_slots', [...watchTimeSlots, newSlot])
    }

    const removeTimeSlot = (index: number) => {
        setValue(
            'time_slots',
            watchTimeSlots.filter((_, i) => i !== index)
        )
    }

    const updateTimeSlot = (
        index: number,
        field: keyof TimeSlotPayload,
        value: string | number
    ) => {
        const updated = [...watchTimeSlots]
        updated[index] = { ...updated[index], [field]: value }
        setValue('time_slots', updated)
    }

    const handleUpdate = async (formData: FormData) => {
        try {
            const payload: UpdateRotationPayload = {
                oncall_rotation_name: formData.oncall_rotation_name,
                shift_frequency: formData.shift_frequency,
                is_active: formData.is_active,
                timing: {
                    timing_type: formData.timing_type,
                    time_slots:
                        formData.timing_type === 'custom_days'
                            ? formData.time_slots
                            : [],
                },
            }
            await doUpdate(payload)
            queryClient.invalidateQueries({
                queryKey: [ONCALL_QUERY_KEYS.ROTATIONS, scheduleId],
            })
            refetch?.()
            onClose()
        } catch (error) {
            console.error(error)
        }
    }

    const handleClose = () => {
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            title={translate('edit_rotation', 'Edit Rotation')}
            maxWidth="sm"
        >
            <form onSubmit={handleSubmit(handleUpdate)}>
                <div className="space-y-4">
                    {/* Rotation Name */}
                    <FormItem
                        control={control}
                        name="oncall_rotation_name"
                        type="textfield"
                        label={`${translate('rotation_name', 'Rotation Name')} *`}
                        placeholder={translate(
                            'rotation_name_placeholder',
                            'e.g., Weekday, Weekend, After-hours'
                        )}
                    />

                    {/* Shift Frequency */}
                    <FormItem
                        control={control}
                        name="shift_frequency"
                        type="select"
                        label={translate('shift_frequency', 'Shift Frequency')}
                        options={[
                            {
                                label: translate('daily', 'Daily'),
                                value: 'daily',
                            },
                            {
                                label: translate('weekly', 'Weekly'),
                                value: 'weekly',
                            },
                        ]}
                    />

                    {/* Timing Type */}
                    <FormItem
                        control={control}
                        name="timing_type"
                        type="select"
                        label={translate('timing_type', 'Timing')}
                        options={[
                            {
                                label: translate('everyday', 'Everyday (24/7)'),
                                value: 'everyday',
                            },
                            {
                                label: translate('custom_days', 'Custom Days'),
                                value: 'custom_days',
                            },
                        ]}
                    />

                    {/* Time Slots - only show for custom_days */}
                    {watchTimingType === 'custom_days' && (
                        <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">
                                    {translate('time_slots', 'Time Slots')}
                                </span>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={addTimeSlot}
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    {translate('add_slot', 'Add Slot')}
                                </Button>
                            </div>

                            {watchTimeSlots.length === 0 ? (
                                <Typogaphy
                                    size="small"
                                    className="text-gray-500 text-center py-4"
                                >
                                    {translate(
                                        'no_time_slots',
                                        'No time slots configured'
                                    )}
                                </Typogaphy>
                            ) : (
                                <div className="space-y-3">
                                    {watchTimeSlots.map((slot, index) => (
                                        <div
                                            key={index}
                                            className="p-3 bg-white rounded border"
                                        >
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="text-xs font-medium text-gray-500">
                                                    {translate('slot', 'Slot')}{' '}
                                                    #{index + 1}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="text-gray-400 hover:text-red-500"
                                                    onClick={() =>
                                                        removeTimeSlot(index)
                                                    }
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                            {/* From Row */}
                                            <div className="mb-3">
                                                <span className="text-xs text-gray-500 mb-1.5 block">
                                                    {translate('from', 'From')}:
                                                </span>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <Select
                                                        fullWidth
                                                        size="small"
                                                        value={slot.day_from}
                                                        onChange={(value) =>
                                                            updateTimeSlot(
                                                                index,
                                                                'day_from',
                                                                Number(value)
                                                            )
                                                        }
                                                        options={DAYS_OF_WEEK.map(
                                                            (d) => ({
                                                                label: translate(
                                                                    d.label
                                                                ),
                                                                value: d.value,
                                                            })
                                                        )}
                                                    />
                                                    <Select
                                                        fullWidth
                                                        size="small"
                                                        value={slot.time_from}
                                                        onChange={(value) =>
                                                            updateTimeSlot(
                                                                index,
                                                                'time_from',
                                                                String(value)
                                                            )
                                                        }
                                                        options={Array.from(
                                                            { length: 48 },
                                                            (_, i) => {
                                                                const hour =
                                                                    Math.floor(
                                                                        i / 2
                                                                    )
                                                                const min =
                                                                    i % 2 === 0
                                                                        ? '00'
                                                                        : '30'
                                                                return {
                                                                    label: `${String(hour).padStart(2, '0')}:${min}`,
                                                                    value: `${String(hour).padStart(2, '0')}:${min}`,
                                                                }
                                                            }
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            {/* To Row */}
                                            <div>
                                                <span className="text-xs text-gray-500 mb-1.5 block">
                                                    {translate('to', 'To')}:
                                                </span>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <Select
                                                        fullWidth
                                                        size="small"
                                                        value={slot.day_to}
                                                        onChange={(value) =>
                                                            updateTimeSlot(
                                                                index,
                                                                'day_to',
                                                                Number(value)
                                                            )
                                                        }
                                                        options={DAYS_OF_WEEK.map(
                                                            (d) => ({
                                                                label: translate(
                                                                    d.label
                                                                ),
                                                                value: d.value,
                                                            })
                                                        )}
                                                    />
                                                    <Select
                                                        fullWidth
                                                        size="small"
                                                        value={slot.time_to}
                                                        onChange={(value) =>
                                                            updateTimeSlot(
                                                                index,
                                                                'time_to',
                                                                String(value)
                                                            )
                                                        }
                                                        options={Array.from(
                                                            { length: 48 },
                                                            (_, i) => {
                                                                const hour =
                                                                    Math.floor(
                                                                        i / 2
                                                                    )
                                                                const min =
                                                                    i % 2 === 0
                                                                        ? '00'
                                                                        : '30'
                                                                return {
                                                                    label: `${String(hour).padStart(2, '0')}:${min}`,
                                                                    value: `${String(hour).padStart(2, '0')}:${min}`,
                                                                }
                                                            }
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Active Toggle */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                            <span className="text-sm font-medium block">
                                {translate('active', 'Active')}
                            </span>
                            <Typogaphy size="small" className="text-gray-500">
                                {translate(
                                    'rotation_active_desc',
                                    'Enable this rotation'
                                )}
                            </Typogaphy>
                        </div>
                        <Switch
                            checked={watchIsActive}
                            onCheckedChange={(checked) =>
                                setValue('is_active', checked)
                            }
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-4 mt-6">
                    <Button
                        variant="outlined"
                        type="button"
                        onClick={handleClose}
                    >
                        {translate('cancel', 'Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        type="submit"
                        loading={isUpdating}
                        disabled={isSubmitting || isUpdating}
                    >
                        {translate('save', 'Save')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default EditRotationModal
